import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Download, MessageSquare, QrCode, Wifi } from 'lucide-react';
import { Card } from '@/components/cards/Card';
import { HERO_CARD } from '../HeroCard';
import { cn } from '@/lib/utils';

type Stage = 'idle' | 'flying' | 'saved' | 'exchanged';

/**
 * Two phones. Tap "Share" on the left one: the card flies to the right
 * phone, lands, and the receiver gets Save / Exchange. Tap Exchange and a
 * lead card appears back on the left. The whole two-way loop, in one demo.
 */
export function ExchangeDemo({ className, dark = true }: { className?: string; dark?: boolean }) {
  const [stage, setStage] = useState<Stage>('idle');
  const [mode, setMode] = useState<'qr' | 'nfc' | 'link'>('qr');

  function share() {
    if (stage !== 'idle') return;
    setStage('flying');
    setTimeout(() => setStage('saved'), 900);
  }

  function reset() {
    setStage('idle');
  }

  return (
    <div className={cn('brand-exchange-demo', className)}>
      {/* Sender */}
      <div className="flex flex-col items-center gap-3">
        <p className={cn('mk-kicker', dark ? 'text-bone/50' : 'text-ink-muted')}>You</p>
        <MiniPhone dark={dark}>
          <div className="space-y-3 p-3">
            <motion.div layoutId="card" className={cn(stage === 'flying' && 'opacity-0')}>
              <Card card={{ ...HERO_CARD, theme_preset: 'editorial' }} className="w-full" compact />
            </motion.div>
            <div className="grid grid-cols-3 gap-1.5">
              {(
                [
                  ['qr', QrCode, 'QR'],
                  ['nfc', Wifi, 'NFC tag'],
                  ['link', Download, 'Link'],
                ] as const
              ).map(([id, Icon, label]) => (
                <button
                  key={id}
                  onClick={() => setMode(id)}
                  aria-pressed={mode === id}
                  className={cn(
                    'flex h-9 items-center justify-center gap-1 rounded-md border text-[10px] font-medium uppercase tracking-widest',
                    mode === id ? 'border-ink bg-ink text-bone' : 'border-bone-deep bg-surface text-ink-muted',
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={stage === 'idle' ? share : reset}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-action text-sm font-medium text-bone"
            >
              {stage === 'idle' ? (mode === 'qr' ? 'Show QR' : mode === 'nfc' ? 'Tap phones' : 'Send link') : 'Reset'}
            </button>
            <AnimatePresence>
              {stage === 'exchanged' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-accent/40 bg-accent/10 p-3"
                >
                  <p className="mk-kicker text-accent">New lead</p>
                  <p className="mt-1 text-sm font-medium text-ink">Jordan Lee</p>
                  <p className="text-[11px] text-ink-muted">jordan@northwind.co · "Loved the print, let's talk"</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </MiniPhone>
      </div>

      {/* Beam */}
      <div className="brand-exchange-beam">
        <div className={cn('mk-horizon h-px w-full', stage === 'flying' && 'animate-pulse')} />
      </div>

      {/* Receiver */}
      <div className="flex flex-col items-center gap-3">
        <p className={cn('mk-kicker', dark ? 'text-bone/50' : 'text-ink-muted')}>Them</p>
        <MiniPhone dark={dark}>
          <div className="flex h-full flex-col p-3">
            <AnimatePresence mode="wait">
              {stage === 'idle' && (
                <motion.div key="idle" exit={{ opacity: 0 }} className="flex flex-1 flex-col items-center justify-center text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-bone-deep text-ink-muted">
                    <QrCode className="h-5 w-5" />
                  </span>
                  <p className="mt-3 text-xs text-ink-muted">
                    {mode === 'qr' ? 'Camera opens the card.' : mode === 'nfc' ? 'A tap opens the card.' : 'The link opens the card.'}
                    <br />
                    No app to install.
                  </p>
                </motion.div>
              )}
              {stage !== 'idle' && (
                <motion.div key="card" className="space-y-3">
                  <motion.div layoutId="card" initial={{ opacity: 0, scale: 0.6, x: -160 }} animate={{ opacity: 1, scale: 1, x: 0 }} transition={{ type: 'spring', stiffness: 220, damping: 24 }}>
                    <Card card={{ ...HERO_CARD, theme_preset: 'editorial' }} className="w-full" compact />
                  </motion.div>
                  {stage !== 'flying' && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 gap-2">
                      <button className="flex h-10 items-center justify-center gap-1.5 rounded-full border border-bone-deep bg-surface text-xs font-medium text-ink">
                        <Download className="h-3.5 w-3.5" />
                        Save to phone
                      </button>
                      <button
                        onClick={() => setStage('exchanged')}
                        className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-ink text-xs font-medium text-bone"
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Exchange
                      </button>
                    </motion.div>
                  )}
                  <AnimatePresence>
                    {stage === 'saved' && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-1.5 text-[11px] text-ink-muted">
                        <Check className="h-3 w-3 text-accent" /> Contact saved. Tap Exchange to send yours back.
                      </motion.p>
                    )}
                    {stage === 'exchanged' && (
                      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-1.5 text-[11px] text-ink-muted">
                        <Check className="h-3 w-3 text-accent" /> Sent. Both sides now hold the other's card.
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </MiniPhone>
      </div>
    </div>
  );
}

function MiniPhone({ children, dark }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <div className={cn('w-full min-w-0 max-w-[250px] rounded-[34px] border p-2', dark ? 'border-bone/15 bg-[#1a1714]' : 'border-bone-deep bg-ink')}>
      <div className="min-h-[430px] overflow-visible rounded-[26px] bg-bone text-ink">{children}</div>
    </div>
  );
}
